import { cast, flow, getEnv, Instance, types } from "mobx-state-tree";
import { emptyVideo, Video } from "../inspection/Video";
import Language from "../translation/language";
import { CodeDetection, ConfidenceLevels, emptyVideoAnalysisDataResponse, GroupCodesWithMainCodes, Segmentation, TimestampPrediction, VideoAnalysisDataResponse } from "../../api-models/response/VideoAnalysisDataResponse";
import { ConfidenceOptions } from "../../pages/analysis/components/video-analysis-diagram/tools/Tools";
import { FileStates } from "../shop/ShopHistoryStorage";
import { endpoints } from "../api/ApiEndpoints";
import { videoPlayerService } from "../video-player/VideoPlayerService";
import { ApplicationError, errorHandler } from "../error-handler/ErrorHandlerService";
import { translationService } from "../translation/TranslationService";
import { featureFlagsStorage } from "../feature-flags/FeatureFlagsStorage";
import { Flags } from "../../generated/FeatureFlags";
import serializeToQueryParams from "../../helpers/QueryParams";
import { ActivityTypes } from "../shop/ActivityPricesStorage";
import { api } from "../api/ApiService";
import { uploadFilesService } from "../upload/UploadFilesService";
import { sortHexBasedOnLuminance } from "../../helpers/ColorHelpers";
import { ServiceModel } from "../app/ServiceStorage";
import { AppStorageModel } from "../app/AppStorage";

export const SegmentationServiceModel = types.compose(
    ServiceModel,
    types.model({
        language: types.enumeration<Language>(Object.values(Language)),
        isOverviewDisplayed: true,
        vaData: types.optional(
            types.frozen<VideoAnalysisDataResponse>(),
            emptyVideoAnalysisDataResponse
        ),
        codesAvailableForDetection: types.optional(
            types.array(types.frozen<GroupCodesWithMainCodes>()),
            []
        ),
        detectedGroupCodes: types.optional(types.array(types.frozen<CodeDetection>()), []),
        detectedMainCodes: types.optional(types.array(types.frozen<CodeDetection>()), []),
        detectedCharacterizations: types.optional(types.array(types.frozen<CodeDetection>()), []),
        showAllSegmentationBars: types.optional(types.boolean, false),
        highlightDetectionConfidenceOption: ConfidenceOptions.Recommended,
        firstTimeChoosingDetectionIndividualConfidence: true,
        shouldShowDetectionIndivididualConfidenceModal: false,
        detectionIndividualConfidenceLevels: types.optional(types.frozen<ConfidenceLevels>(), {}),
        highlightDetectionOverallConfidence: types.optional(types.number, 0.75),
    })
).views((self) => ({
    get predictionsForSmallestAccuracy() {
        const flatted = self.codesAvailableForDetection
          .map((code) => code.mainCodesWithSecondaryCodes)
          .flat();
  
        let combinedPredictions: TimestampPrediction[] = [];
  
        for (const mainCode of flatted) {
          combinedPredictions = [
            ...combinedPredictions,
            ...(Object.values(
              self.vaData.predictionsPerAccuracy[`${mainCode.mainCode}_${"0.65"}`]
            ) as TimestampPrediction[]),
          ];
        }
  
        return combinedPredictions;
    },
    get segmentationNotStarted(): boolean {
        return self.status === FileStates.None;
    },
    get segmentationRunning(): boolean {
        return self.status === FileStates.Running;
    },
    get segmentationFailed(): boolean {
        return self.status === FileStates.Failed;
    },
    get segmentationSuccessful(): boolean {
        return self.status === FileStates.Success;
    },
})).actions((self) => ({
    isGroupCodeDetected: (groupCode: string): boolean => {
        return self.vaData.segmentationDetails.segmentations.some(
          (x: Segmentation) => x.groupCode === groupCode && x.coordinates.length > 0
        );
    },
    getSortedCodesByColor: (codes: GroupCodesWithMainCodes[]): GroupCodesWithMainCodes[] => {
        const sortedCodes: GroupCodesWithMainCodes[] = [];
  
        codes.forEach((code) => {
          sortedCodes.push({
            groupCode: code.groupCode,
            color: code.color,
            mainCodesWithSecondaryCodes: code.mainCodesWithSecondaryCodes.sort((code1, code2) =>
              sortHexBasedOnLuminance(code1.color, code2.color)
            ),
          });
        });
  
        return sortedCodes;
    },

    isMainCodeDetected: (mainCode: string): boolean => {
        return self.predictionsForSmallestAccuracy.some(
          (x) => x.predictionDetails?.mainCode === mainCode
        );
    }
})).extend(self => {
    return {
        actions: {
            clear() {
                self.fileId = undefined;
                self.video = undefined;
                self.status = FileStates.None
            },
            request: flow(function* () {
                const languageQueryParam = translationService.language === Language.english ? 0 : 1;
                if (featureFlagsStorage.isFeatureEnabled(Flags.Render_VA_Diagram_In_Platform)) {
                const endpointVAData = endpoints.get("video_analysis_data");
                if (endpointVAData) {
                    endpointVAData.routeParams = [self.fileId!];
                }

                var responseVAData:any = {};
                try {
                    responseVAData = yield getEnv(self).api.get(endpointVAData);
                } catch (error) {
                    self.video = new Video({...emptyVideo, isConverted: true, analysisSuccessfullyDone: false, segmentationSuccessfullyDone: false});
                }

                self.video = new Video(responseVAData.video);
                self.status = self.getFileState(
                    responseVAData.segmentationWasSuccessful, 
                    responseVAData.segmentationErrorMessage,
                    responseVAData.segmentationIsInProgress);
                if (self.status !== FileStates.Success) return;
                self.codesAvailableForDetection = cast(
                    self.getSortedCodesByColor(responseVAData.codesAvailableForDetection)
                );
                self.vaData = responseVAData;

                self.highlightDetectionOverallConfidence =
                    self.vaData.recommendedConfidenceLevels["Highlight_Detection"];

                const mainCodes: CodeDetection[] = [];
                self.codesAvailableForDetection.forEach((groupCode) => {
                    groupCode.mainCodesWithSecondaryCodes.forEach((mainCode) => {
                    mainCodes.push({
                        code: mainCode.mainCode,
                        colorCode: mainCode.color,
                        isVisible: true,
                        secondaryCodes: mainCode.secondaryCodes,
                        isDetected: self.isMainCodeDetected(mainCode.mainCode),
                        groupCode: groupCode.groupCode,
                    });
                    });
                });

                self.detectedMainCodes = cast(mainCodes);

                const characterizationCodes: CodeDetection[] = [];
                for (let key in self.vaData.characterizationCodesWithColors) {
                    characterizationCodes.push({
                    code: key,
                    colorCode: self.vaData.characterizationCodesWithColors[key],
                    isVisible: true,
                    isDetected: self.isMainCodeDetected(key),
                    });
                }
                self.detectedCharacterizations = cast(characterizationCodes);

                if (featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations)) {
                    const groupCodes: CodeDetection[] = [];
                    self.codesAvailableForDetection.forEach((groupCode) => {
                        groupCodes.push({
                            code: groupCode.groupCode,
                            colorCode: groupCode.color,
                            isVisible: true,
                            isDetected: self.isGroupCodeDetected(groupCode.groupCode),
                        });
                    });
                    self.detectedGroupCodes = cast(groupCodes);
                    videoPlayerService.setGroupCodesFilterOptions(self.detectedGroupCodes);
                }
                } else {
                    const endpoint = endpoints.get("media_file_video_analysis");
                    if (endpoint) {
                        endpoint.routeParams = [self.fileId!];
                        endpoint.queryParams = serializeToQueryParams({ language: languageQueryParam });
                    }
                    const response = yield getEnv(self).api.get(endpoint);
                    self.status = self.getFileState(
                        responseVAData.segmentationSuccessful, 
                        responseVAData.segmentationErrorMessage,
                        responseVAData.segmentationIsInProgress);
                }
            }),
            runService: flow(function* () {
                try {
                    const endpoint = endpoints.get("media_file_segmentation");
                    if (endpoint) {
                      endpoint.routeParams = [self.fileId!];
                    }
                    self.status = FileStates.Running;
                    videoPlayerService.setGroupCodesFilterOptions([]);
                    yield getEnv(self).api.put(endpoint);
                } catch (error) {
                    self.status = FileStates.None;
                    const applicationError = error as ApplicationError;
                    getEnv(self).errorHandler.handleErrorInSnackbar(applicationError);
                    throw applicationError;
                }
            }),
            runAfterUpload: flow(function* () {
                yield self.startService();
                yield self.load();
            })
        }
    }
}).actions((self) => ({
    getGroupCodeColors(): any {
        return self.codesAvailableForDetection.reduce((accumulator, value) => {
          return { ...accumulator, [value.groupCode.toLowerCase()]: value.color };
        }, {});
    },
    setShowAllSegmentationBars(showAll: boolean) {
        self.showAllSegmentationBars = showAll;
    },
    setHighlightDetectionConfidenceOption(confidenceOption: ConfidenceOptions) {
        self.highlightDetectionConfidenceOption = confidenceOption;
    },
    setIndividualDetectionConfidence(confidenceLevels: ConfidenceLevels) {
        self.detectionIndividualConfidenceLevels = confidenceLevels;
        self.firstTimeChoosingDetectionIndividualConfidence = false;
    },
    setHighlightDetectionOverallConfidence(overallConfidence: number) {
        self.highlightDetectionOverallConfidence = overallConfidence;
    },
    toggleGroupCodeVisibility(code: string) {
        const updatedOptions = self.detectedGroupCodes.map((option) =>
          option.code === code ? { ...option, isVisible: !option.isVisible } : option
        );

        self.detectedGroupCodes = cast(updatedOptions);
    },
    showDetectionIndivididualConfidenceModal() {
        self.shouldShowDetectionIndivididualConfidenceModal = true;
    },
    hideDetectionIndivididualConfidenceModal() {
        self.shouldShowDetectionIndivididualConfidenceModal = false;
    },
}))

export type SegmentationService = Instance<typeof SegmentationServiceModel>;